/**
 * Score watching — the two buttons and the slide-up panel on the fan screen.
 *
 * The fan never leaves the page and the phone never calls the league: both
 * lists come from our own server, which caches them. Team logos are league
 * assets on a league domain, so only abbreviations are drawn here.
 *
 * A goal always wins: when a round opens the panel closes itself, because the
 * scratch card is the reason the fan is holding the phone.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ListOrdered, Radio, X } from 'lucide-react';
import { GoalTapelligenceFooter } from './GoalTapelligenceFooter';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api/goal-game`;

/** ONE look for the four fan buttons — Live scores, Standings, Tonight's
 *  contest and Tonight's stats. Same height (44px tap target), same corner,
 *  same border and the same text size and weight. Imported by GoalFanView so
 *  the two pairs can never drift apart again. */
export const FAN_BAR_BTN = 'flex-1 min-h-[44px] inline-flex items-center justify-center gap-2 rounded-xl border border-white/25 px-3 py-2.5 text-sm font-bold text-white whitespace-nowrap';
/** Both groups line up with the fan card above (max-w-sm), centred, so an
 *  iPad does not stretch them across the whole screen. Four children = a
 *  2 x 2 grid on every screen size. */
export const FAN_BAR_ROW = 'mx-auto grid w-full max-w-sm grid-cols-2 gap-2';

/** The black bar itself. Exported so the fan screen can still draw the two
 *  Tonight buttons in the same bar when a league has no score watching.
 *
 *  On the iPad demo link the operator bar sits under it. That bar's height
 *  changes with the league row, so it is MEASURED (read only — nothing on the
 *  operator bar is changed) and this bar is lifted by exactly that much. */
export const FanBar = ({ framed = false, raised = false, full = null, children }) => {
    const [lift, setLift] = useState(64);
    useEffect(() => {
        if (!raised) return undefined;
        const read = () => {
            const el = document.querySelector('[data-testid="goal-op-bar"]');
            if (el) setLift(Math.round(el.getBoundingClientRect().height));
        };
        read();
        const t = setInterval(read, 1000);
        window.addEventListener('resize', read);
        return () => { clearInterval(t); window.removeEventListener('resize', read); };
    }, [raised]);
    return (
        <div className={`${framed ? 'absolute' : 'fixed'} left-0 right-0 z-30 px-3 py-2 border-t border-white/15`}
             style={{ backgroundColor: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)',
                      bottom: raised ? `${lift}px` : 0 }}
             data-testid="sw-bar">
            <div className="mx-auto w-full max-w-sm space-y-2">
                {full}
                <div className={FAN_BAR_ROW} data-testid="goal-fan-buttons">{children}</div>
            </div>
        </div>
    );
};
const CONFS = ['eastern', 'western'];
const SCORES_EVERY = 20000;
const STANDINGS_EVERY = 60000;

const startTime = (iso, lang) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleTimeString(lang === 'fr' ? 'fr-CA' : 'en-CA',
                                                { hour: 'numeric', minute: '2-digit' });
    } catch (_e) { return ''; }
};

const dayLabel = (date, lang) => {
    if (!date) return '';
    try {
        const [y, m, d] = date.split('-').map(Number);
        return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(
            lang === 'fr' ? 'fr-CA' : 'en-CA',
            { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
    } catch (_e) { return date; }
};

const Row = ({ game, lang, c, t }) => (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-white/10 last:border-0"
         data-testid={`sw-game-${game.id}`}>
        <div className="min-w-0">
            <p className="text-sm font-semibold tabular-nums">
                {game.away.abbrev} {game.away.score ?? ''} <span className="opacity-40">@</span> {game.home.abbrev} {game.home.score ?? ''}
            </p>
        </div>
        <div className="text-right shrink-0">
            {game.state === 'live' ? (
                <span className="text-xs font-bold" style={{ color: c.button }}>
                    {game.intermission
                        ? t('goalGame.scoreIntermission')
                        /* Outside hockey the league's own one-line status says
                           it right: "Q4 2:31", "Top 5th", "45'". */
                        : (game.status_detail || `P${game.period} ${game.clock || ''}`)}
                </span>
            ) : game.state === 'final' ? (
                <span className="text-xs opacity-50">{t('goalGame.scoreFinal')}</span>
            ) : (
                <span className="text-xs opacity-50">{startTime(game.start_time_utc, lang)}</span>
            )}
        </div>
    </div>
);

/** A sport's own table. `columns` comes from the server, one set per league, so
 *  hockey points and OT losses are never shown for basketball or baseball. */
const LeagueTable = ({ teams, columns, c }) => (
    <table className="w-full text-xs tabular-nums">
        <thead className="opacity-50">
            <tr className="text-left">
                <th className="font-medium py-1">&nbsp;</th>
                {columns.map((col) => (
                    <th key={col.key} className="font-medium text-right">{col.label}</th>
                ))}
            </tr>
        </thead>
        <tbody>
            {teams.map((team) => (
                <tr key={team.abbrev} className="border-t border-white/10" data-testid={`sw-team-${team.abbrev}`}>
                    <td className="py-1.5 font-semibold">{team.abbrev}</td>
                    {columns.map((col, i) => (
                        <td key={col.key}
                            className={i === columns.length - 1 ? 'text-right font-bold' : 'text-right opacity-70'}
                            style={i === columns.length - 1 ? { color: c.button } : undefined}>
                            {team.values?.[col.key] ?? ''}
                        </td>
                    ))}
                </tr>
            ))}
        </tbody>
    </table>
);

const Table = ({ teams, c, t }) => (
    <table className="w-full text-xs tabular-nums">
        <thead className="opacity-50">
            <tr className="text-left">
                <th className="font-medium py-1">{t('goalGame.standTeam')}</th>
                <th className="font-medium text-right">GP</th>
                <th className="font-medium text-right">W</th>
                <th className="font-medium text-right">L</th>
                <th className="font-medium text-right">OTL</th>
                <th className="font-medium text-right">{t('goalGame.standPoints')}</th>
            </tr>
        </thead>
        <tbody>
            {teams.map((team) => (
                <tr key={team.abbrev} className="border-t border-white/10" data-testid={`sw-team-${team.abbrev}`}>
                    <td className="py-1.5 font-semibold">{team.abbrev}</td>
                    <td className="text-right opacity-70">{team.games_played}</td>
                    <td className="text-right opacity-70">{team.wins}</td>
                    <td className="text-right opacity-70">{team.losses}</td>
                    <td className="text-right opacity-70">{team.ot_losses}</td>
                    <td className="text-right font-bold" style={{ color: c.button }}>{team.points}</td>
                </tr>
            ))}
        </tbody>
    </table>
);

export const GoalScoreWatch = ({ cardId, lang, t, brand, c, ty, logoUrl, goalLive,
                                primaryBtn, ghostBtn, scoresBtn, standingsBtn,
                                topRow = null, fullRow = null, onButtonTap = null,
                                hideBar = false, requestTab = null, onTabClosed = null,
                                framed = false, raised = false }) => {
    const [tab, setTab] = useState(null);            // 'scores' | 'standings' | null
    const [conf, setConf] = useState(null);
    const [groups, setGroups] = useState(null);      // [{id,label}] from the server
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [banner, setBanner] = useState(null);
    const lastScores = useRef({});

    const close = useCallback(() => {
        setTab(null); setConf(null); setData(null); setBanner(null);
        try { onTabClosed?.(); } catch (_e) { /* never blocks */ }
    }, [onTabClosed]);

    // A goal takes priority over everything on this screen.
    useEffect(() => { if (goalLive) close(); }, [goalLive, close]);

    // The main page's own Live scores / Standings buttons ask for a panel here.
    useEffect(() => {
        if (!requestTab) return;
        setTab(requestTab); setConf(null); setData(null); lastScores.current = {};
    }, [requestTab]);

    // Which groupings this league offers: NHL conferences, AFC/NFC,
    // American/National, or college football's conferences. Read once.
    useEffect(() => {
        if (!cardId) return undefined;
        let alive = true;
        (async () => {
            try {
                const res = await fetch(`${API}/public/${cardId}/score-groups`);
                const body = res.ok ? await res.json() : null;
                if (alive) setGroups(body?.groups?.length ? body.groups : []);
            } catch (_e) { if (alive) setGroups([]); }
        })();
        return () => { alive = false; };
    }, [cardId]);

    const load = useCallback(async (which, conference, quiet) => {
        if (!cardId || !conference) return;
        if (!quiet) setLoading(true);
        try {
            const res = await fetch(`${API}/public/${cardId}/${which === 'scores' ? 'scores' : 'standings'}?conference=${conference}`);
            const body = res.ok ? await res.json() : null;
            if (!body) { setData((prev) => prev || { available: false }); return; }

            if (which === 'scores') {
                // Any goal anywhere in the league, while the panel is open.
                const seen = lastScores.current;
                for (const g of body.games || []) {
                    const now = (g.home.score || 0) + (g.away.score || 0);
                    const before = seen[g.id];
                    if (before != null && now > before) {
                        setBanner(`${g.away.abbrev} ${g.away.score} — ${g.home.abbrev} ${g.home.score}`);
                        setTimeout(() => setBanner(null), 8000);
                    }
                    seen[g.id] = now;
                }
            }
            setData(body);
        } catch (_e) {
            setData((prev) => prev || { available: false });
        } finally { setLoading(false); }
    }, [cardId]);

    // Poll while the panel is open AND the page is visible, nothing otherwise.
    useEffect(() => {
        if (!tab || !conf) return undefined;
        load(tab, conf, false);
        const every = tab === 'scores' ? SCORES_EVERY : STANDINGS_EVERY;
        const tick = () => { if (document.visibilityState === 'visible') load(tab, conf, true); };
        const id = setInterval(tick, every);
        return () => clearInterval(id);
    }, [tab, conf, load]);

    const openTab = (which) => { try { onButtonTap?.(which); } catch (_e) { /* never blocks */ } setTab(which); setConf(null); setData(null); lastScores.current = {}; };

    const barPos = framed ? 'absolute' : 'fixed';
    // THE FAN SCREEN HAS NO BOTTOM BAR ANY MORE — every one of its buttons
    // lives in the main page's own list, so the bar would be a second copy.
    // The slide-up panels still belong here; the page asks for them through
    // `requestTab`.
    const bar = hideBar ? null : (
        <FanBar framed={framed} raised={raised} full={fullRow}>
            {topRow}
            <button type="button" onClick={() => openTab('scores')} data-testid="sw-open-scores"
                    style={scoresBtn || ghostBtn}
                    className={FAN_BAR_BTN}>
                <Radio size={14} />{t('goalGame.liveScores')}
            </button>
            <button type="button" onClick={() => openTab('standings')} data-testid="sw-open-standings"
                    style={standingsBtn || ghostBtn}
                    className={FAN_BAR_BTN}>
                <ListOrdered size={14} />{t('goalGame.standings')}
            </button>
        </FanBar>
    );

    if (!tab) return bar;

    const age = data?.age_seconds;
    const stamp = age == null ? null
        : t('goalGame.scoreUpdated', { seconds: age });

    return (
        <>
            {bar}
            <div className={`${barPos} inset-0 z-40 flex items-end`} data-testid="sw-panel-wrap">
                <button type="button" aria-label="close" onClick={close} data-testid="sw-scrim"
                        className="absolute inset-0 bg-black/60" />
                <div className="relative w-full max-h-[88%] overflow-y-auto rounded-t-3xl border-t border-white/15 px-4 pt-4 pb-8 sw-rise"
                     style={{ backgroundColor: c.bg || '#0b1220', color: c.text, ...ty.body }}
                     data-testid="sw-panel">
                    <div className="flex items-center justify-between gap-3 mb-4">
                        {logoUrl ? (
                            <img src={logoUrl} alt={brand?.organization_name || ''} data-testid="sw-panel-logo"
                                 style={{ height: `${Math.min(brand?.logo_size_px || 40, 48)}px` }}
                                 className="object-contain" />
                        ) : (
                            <p className="text-sm font-semibold opacity-80" data-testid="sw-panel-logo">
                                {brand?.organization_name || ''}
                            </p>
                        )}
                        <button type="button" onClick={close} data-testid="sw-close"
                                style={ghostBtn}
                                className="rounded-full border border-white/25 p-1.5">
                            <X size={16} />
                        </button>
                    </div>

                    <p className="text-lg font-bold mb-3" style={ty.headline} data-testid="sw-panel-title">
                        {tab === 'scores' ? t('goalGame.liveScores') : t('goalGame.standings')}
                    </p>

                    {banner && (
                        <div className="mb-3 rounded-xl px-3 py-2 text-sm font-black text-center"
                             style={{ backgroundColor: c.button, color: '#fff' }} data-testid="sw-goal-banner">
                            {t('goalGame.goalBanner')} — {banner}
                        </div>
                    )}

                    {!conf ? (
                        <div className="grid gap-3" data-testid="sw-conference-choice">
                            {(groups === null ? CONFS.map((id) => ({ id, label: null }))
                                : groups).map((g) => (
                                <button key={g.id} type="button" onClick={() => setConf(g.id)}
                                        data-testid={`sw-conf-${g.id}`}
                                        style={primaryBtn || { backgroundColor: c.button, color: '#ffffff' }}
                                        className="w-full rounded-2xl py-5 text-lg font-black shadow-lg">
                                    {CONFS.includes(g.id) ? t(`goalGame.conf_${g.id}`) : g.label}
                                </button>
                            ))}
                            {groups !== null && groups.length === 0 && (
                                <p className="py-6 text-center text-sm opacity-70" data-testid="sw-no-groups">
                                    {tab === 'scores' ? t('goalGame.scoresUnavailable') : t('goalGame.standingsUnavailable')}
                                </p>
                            )}
                        </div>
                    ) : (
                        <>
                            <button type="button" onClick={() => { setConf(null); setData(null); }}
                                    data-testid="sw-conf-back"
                                    className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold opacity-70">
                                <ChevronLeft size={14} />{t('goalGame.conferences')}
                            </button>

                            {loading && !data ? (
                                <p className="py-6 text-center text-sm opacity-60" data-testid="sw-loading">…</p>
                            ) : !data || data.available === false ? (
                                <p className="py-6 text-center text-sm opacity-70" data-testid="sw-unavailable">
                                    {tab === 'scores' ? t('goalGame.scoresUnavailable') : t('goalGame.standingsUnavailable')}
                                </p>
                            ) : tab === 'scores' ? (
                                <div data-testid="sw-scores">
                                    {(data.games || []).length === 0 ? (
                                        <>
                                            <p className="py-3 text-sm font-semibold" data-testid="sw-no-games">
                                                {t('goalGame.noGamesTonight')}
                                            </p>
                                            {(data.next_games || []).length > 0 && (
                                                <>
                                                    <p className="mt-2 mb-1 text-xs uppercase tracking-wider opacity-60"
                                                       data-testid="sw-next-day">
                                                        {t('goalGame.nextGameDay')} — {dayLabel(data.next_date, lang)}
                                                    </p>
                                                    {data.next_games.map((g) => (
                                                        <Row key={g.id} game={g} lang={lang} c={c} t={t} />
                                                    ))}
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        data.games.map((g) => <Row key={g.id} game={g} lang={lang} c={c} t={t} />)
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-5" data-testid="sw-standings">
                                    {(data.divisions || []).map((div) => (
                                        <div key={div.name}>
                                            <p className="text-xs uppercase tracking-wider opacity-60 mb-1">{div.name}</p>
                                            {data.columns?.length
                                                ? <LeagueTable teams={div.teams} columns={data.columns} c={c} />
                                                : <Table teams={div.teams} c={c} t={t} />}
                                        </div>
                                    ))}
                                    {(data.wildcard || []).length > 0 && (
                                        <div>
                                            <p className="text-xs uppercase tracking-wider opacity-60 mb-1"
                                               data-testid="sw-wildcard">{t('goalGame.wildCard')}</p>
                                            <Table teams={data.wildcard} c={c} t={t} />
                                        </div>
                                    )}
                                </div>
                            )}

                            {stamp && (
                                <p className="mt-4 text-[11px] opacity-50" data-testid="sw-updated">
                                    {data?.stale ? `${t('goalGame.scoresUnavailable')} ${stamp}` : stamp}
                                </p>
                            )}
                        </>
                    )}
                    <GoalTapelligenceFooter />
                </div>
            </div>
        </>
    );
};

export default GoalScoreWatch;
